import { Icon, Switch, Table, Text, Tooltip } from '@dixa/lib-shared-habla';
import { typography } from '@dixa/lib-shared-tokens';
import {
  RoleAuthorizationScope,
  RoleAuthorizationScopeValue,
  RoleWithScopes,
} from 'lib-shared-user-role';
import groupBy from 'lodash/groupBy';
import { memo, useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSet, useUpdateEffect } from 'react-use';

import { rolesApi } from 'apis/roles';

import { SettingsAgentsRoleFormValues } from '../settingsAgentsRoleForm.types';

import styles from './roleFormAuthorizationScopesField.module.scss';

type RoleFormAuthorizationScopesFieldProps = {
  roleWithScopes?: RoleWithScopes;
};

export const RoleFormAuthorizationScopesField = memo(
  function RoleFormAuthorizationScopesField({
    roleWithScopes,
  }: RoleFormAuthorizationScopesFieldProps): JSX.Element {
    const { categorizedScopes, isLoading, isError } =
      rolesApi.useReadAuthorizationScopesQuery(undefined, {
        selectFromResult: ({ data, ...rest }) => ({
          ...rest,
          categorizedScopes: groupBy<RoleAuthorizationScope>(
            data?.data?.authorization_scopes ?? [],
            'category',
          ),
        }),
      });

    const form = useFormContext<SettingsAgentsRoleFormValues>();

    const [scopes, { has, add, toggle, remove }] =
      useSet<RoleAuthorizationScopeValue>(new Set(form.getValues('scopes')));

    // Handles propagating the selected scopes to the form state.
    useUpdateEffect(() => {
      form.setValue('scopes', Array.from(scopes), {
        shouldDirty: true,
        shouldValidate: true,
      });
    }, [scopes]);

    /**
     * Whether or not a category is toggled, if at least one of the scopes
     * belonging to the category is enabled.
     */
    const isCategoryToggled = useCallback(
      (categoryScopes: readonly RoleAuthorizationScope[]): boolean => {
        return categoryScopes.some(({ value }) => has(value));
      },
      [has],
    );

    /**
     * Handles toggling an entire category of scopes on or off. If at least one
     * category scope is enabled, it disables them all. If none are enabled, it
     * enables them all.
     */
    const handleToggleCategory = useCallback(
      (categoryScopes: readonly RoleAuthorizationScope[]) => {
        categoryScopes.forEach(({ value }) =>
          isCategoryToggled(categoryScopes) ? remove(value) : add(value),
        );
      },
      [add, isCategoryToggled, remove],
    );

    const initiallyExpandedRows = useMemo(
      () =>
        Object.entries(categorizedScopes)
          .filter(([_, categoryScopes]) => isCategoryToggled(categoryScopes))
          .map(([categoryLabel]) => categoryLabel),
      [categorizedScopes, isCategoryToggled],
    );

    const theadRow = (
      <Table.Tr>
        <Table.Th width="50%">Permission group</Table.Th>
        <Table.Th width="25%">Access</Table.Th>
        <Table.Th width="25%">Description</Table.Th>
      </Table.Tr>
    );

    return (
      <Table
        className={styles.table}
        expandableRowsProps={{
          expandableRows: true,
          initiallyExpandedRows,
        }}
        loading={isLoading}
        loadingFailed={isError}
      >
        <Table.Thead>{theadRow}</Table.Thead>

        <Table.Tbody>
          {Object.entries(categorizedScopes).map(
            ([categoryLabel, categoryScopes]) => (
              <Table.Tr
                key={categoryLabel}
                expandableContent={
                  <Table framed={false} headerWithBackground={false}>
                    <Table.Thead className={styles.subThead}>
                      {theadRow}
                    </Table.Thead>

                    <Table.Tbody>
                      {categoryScopes.map((scope) => (
                        <Table.Tr key={scope.value}>
                          <Table.Td>
                            <Text.Paragraph className={styles.label}>
                              {scope.label}
                            </Text.Paragraph>
                          </Table.Td>

                          <Table.Td>
                            <Switch
                              checked={has(scope.value)}
                              onChange={() => toggle(scope.value)}
                              name={scope.value}
                              value={scope.value}
                              disabled={roleWithScopes?.role.is_system_role}
                            />
                          </Table.Td>

                          <Table.Td>
                            <Tooltip content={scope.description} dark>
                              <Icon
                                name="info"
                                size={20}
                                color={typography.color.secondary}
                              />
                            </Tooltip>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                }
                rowId={categoryLabel}
              >
                <Table.Td>{categoryLabel}</Table.Td>

                <Table.Td>
                  <Switch
                    checked={isCategoryToggled(categoryScopes)}
                    onChange={() => handleToggleCategory(categoryScopes)}
                    name={categoryLabel}
                    value={categoryLabel}
                    disabled={roleWithScopes?.role.is_system_role}
                  />
                </Table.Td>

                <Table.Td>
                  <Tooltip content={categoryLabel} dark>
                    <Icon
                      name="info"
                      size={20}
                      color={typography.color.secondary}
                    />
                  </Tooltip>
                </Table.Td>
              </Table.Tr>
            ),
          )}
        </Table.Tbody>
      </Table>
    );
  },
);
